import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { concatLatestFrom } from '@ngrx/operators'
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators'
import { RankingsApi } from '../../api/rankings.api'
import { ModalController } from '@ionic/angular'
import { EMPTY, Observable } from 'rxjs'
import { fromPromise } from 'rxjs/internal/observable/innerFrom'
import { Store } from '@ngrx/store'
import { resolveTeamRankingRequestAdapter } from '../../adapters/team-ranking.adapter'
import { RankedMetric, RankingContext, RankingRequest, RankingWarningDialog } from '../../models/ranking.model'
import { RankingWarningMode } from '../../enums/ranking.enums'
import { RankingContainerComponent } from '../../ranking-container.component'
import { resolvePlayerRankingRequestAdapter } from '../../adapters/player-ranking.adapter'
import { resolveMatchRankingRequest } from '../../adapters/match-ranking.adapter'
import { RANKING_MATCH_PREVIOUS_STATES } from '@features/rankings/constants/rankings.constants'
import {
  selectMetricToRank,
  selectRankedMetricContext,
  selectRankedMetricDisplayMode
} from '@features/rankings/store/selectors/ranking.selectors'
import { DemarcationPipe } from '@shared/pipes/demarcation.pipe'
import {
  PLAYER_DEMARCATION_FALLBACK_KEY,
  PLAYER_DEMARCATION_KEYS,
  PLAYER_POSITION_KEYS
} from '@core/constants/player.constants'
import {
  closeRankingModal,
  fetchRanking,
  openRankingModal,
  openRankingWarningModal,
  performFetchRanking,
  purgeRanking,
  refreshRanking,
  setRanking,
  setRankingContext,
  setRankingLoader,
  setRankingMatchDay,
  setRankingSourceMetric
} from '@features/rankings/store/actions/ranking.actions'
import { DisplayMode } from '@core/enums/display.enums'
import { Match } from '@core/models/match.models'
import { ContextMenuItemEvent } from '@core/models/context-menu.models'
import { partition } from 'lodash-es'
import { excludeNegativeRankingFromSortFn } from '@features/rankings/utils/ranking.utils'
import {
  selectAggregationContextMetadata,
  selectAggregationsContext,
  selectAggregationToRank
} from '@features/aggregations/store/selectors/aggregations.selectors'
import { AggregationContext, AggregationSelectable } from '@features/aggregations/models/aggregations.models'
import { WarningModalComponent } from '@shared/components/warning-modal/warning-modal.component'
import { MetricFinderKey } from '@core/enums/metric.enums'
import { selectMergedRouteData } from '@core/ngrx-router/state/selectors/ngrx-router.selectors'
import { CallingContext } from '@features/seasons/models/seasons.models'
import { MatchState } from '@mediacoach/ui'

@Injectable()
export class RankingEffects {
  private readonly _actions: Actions = inject(Actions)
  private readonly _api: RankingsApi = inject(RankingsApi)
  private readonly _store: Store = inject(Store)
  private readonly _modalController: ModalController = inject(ModalController)
  private readonly _demarcationPipe: DemarcationPipe<any> = inject(DemarcationPipe<any>)
  private _modal: HTMLIonModalElement

  fetchRankedMetric$ = createEffect(() =>
    this._actions.pipe(
      ofType(fetchRanking),
      map(({ payload }) => {
        if (
          !/previa/i.test(payload.value?.keyPath) &&
          RANKING_MATCH_PREVIOUS_STATES.includes((payload?.valueContext as Match)?.state?.name as MatchState)
        ) {
          return { warning: RankingWarningMode.NoMatch, payload: 'MTR_RANKINGS_NO_MATCH_DATA' }
        }
        return { warning: null, payload }
      }),
      map(({ warning, payload }) =>
        warning
          ? openRankingWarningModal({ body: payload as string })
          : performFetchRanking({ payload: payload as ContextMenuItemEvent })
      )
    )
  )

  performFetchRanking$ = createEffect(() =>
    this._actions.pipe(
      ofType(performFetchRanking),
      concatLatestFrom(() => [
        this._store.select(selectAggregationToRank),
        this._store.select(selectAggregationsContext),
        this._store.select(selectAggregationContextMetadata),
        this._store.select(selectMergedRouteData)
      ]),
      map(([{ payload }, aggregation, context, aggregationMetadata, routeData]) => ({
        originalPayload: payload,
        resolvedPayload: this._resolvePayload(payload, aggregation, context, aggregationMetadata),
        routeData
      })),
      tap(({ resolvedPayload, routeData }) => {
        this._store.dispatch(setRankingLoader({ loading: true }))
        this._store.dispatch(setRankingContext({ ...resolvedPayload }))
        this._store.dispatch(
          openRankingModal({
            payload: { ...resolvedPayload, callingContext: routeData['callingContext'] as CallingContext }
          })
        )
      }),
      switchMap(({ originalPayload, resolvedPayload: payload }) =>
        this._api.fetchRankedMetric(payload.metricToRank).pipe(
          map(({ ranking, matchDay }) => ({ matchDay, ranking: this._mapAndSortRanking(ranking) })),
          mergeMap(({ ranking, matchDay }) => [
            setRankingSourceMetric({ sourceMetric: originalPayload.value }),
            setRanking({ ranking }),
            setRankingMatchDay({ matchDay }),
            setRankingLoader({ loading: false })
          ]),
          catchError((err) => {
            this._store.dispatch(setRankingLoader({ loading: false }))
            console.error(err)
            return EMPTY
          })
        )
      )
    )
  )

  refreshRanking$ = createEffect(() =>
    this._actions.pipe(
      ofType(refreshRanking),
      concatLatestFrom(() => [
        this._store.select(selectMetricToRank),
        this._store.select(selectRankedMetricContext),
        this._store.select(selectRankedMetricDisplayMode)
      ]),
      tap(() => this._store.dispatch(setRankingLoader({ loading: true }))),
      map(([{ aggregation }, metricToRank, metricContext, displayMode]) => ({
        metricToRank: { ...metricToRank, aggregationDataSource: aggregation },
        metricContext,
        displayMode
      })),
      switchMap(({ metricToRank, metricContext, displayMode }) =>
        this._api.fetchRankedMetric(metricToRank).pipe(
          map(({ ranking, matchDay }) => ({ matchDay, ranking: this._mapAndSortRanking(ranking) })),
          mergeMap(({ ranking }) => [
            setRankingContext({ metricToRank, metricContext, displayMode }),
            setRanking({ ranking }),
            setRankingLoader({ loading: false })
          ]),
          catchError((err) => {
            this._store.dispatch(setRankingLoader({ loading: false }))
            console.error(err)
            return EMPTY
          })
        )
      )
    )
  )

  openWarningModal$ = createEffect(
    () =>
      this._actions.pipe(
        ofType(openRankingWarningModal),
        switchMap(({ body }) =>
          this._createWarningModal({
            title: 'MTR_COMMON_WARNING',
            body,
            actionClose: closeRankingModal
          })
        ),
        switchMap((modal) => {
          this._modal = modal
          return fromPromise(this._modal.present())
        })
      ),
    { dispatch: false }
  )

  openRankingsModal$ = createEffect(
    () =>
      this._actions.pipe(
        ofType(openRankingModal),
        switchMap(({ payload }) =>
          this._createRankingModal(payload).pipe(
            switchMap((modal) => {
              this._modal = modal
              return fromPromise(this._modal.present())
            })
          )
        )
      ),
    { dispatch: false }
  )

  closeRankingsModal$ = createEffect(() =>
    this._actions.pipe(
      ofType(closeRankingModal),
      switchMap(() => fromPromise(this._modal?.dismiss())),
      map(() => purgeRanking())
    )
  )

  private _createWarningModal(componentProps: RankingWarningDialog): Observable<HTMLIonModalElement> {
    return fromPromise(
      this._modalController.create({
        component: WarningModalComponent,
        componentProps,
        cssClass: 'mcm-rankings-warning',
        backdropDismiss: true
      })
    )
  }

  private _createRankingModal(componentProps: RankingContext): Observable<HTMLIonModalElement> {
    return fromPromise(
      this._modalController.create({
        component: RankingContainerComponent,
        componentProps,
        cssClass: 'mcm-rankings',
        backdropDismiss: true
      })
    )
  }

  private _resolvePayload(
    payload: ContextMenuItemEvent,
    aggregation: AggregationSelectable,
    context: MetricFinderKey,
    contextMetadata: AggregationContext
  ): RankingContext {
    let metricToRank: RankingRequest
    let displayMode: DisplayMode

    switch (context) {
      case MetricFinderKey.Team:
        metricToRank = resolveTeamRankingRequestAdapter(payload, aggregation.id, contextMetadata)
        displayMode = contextMetadata.isVsEnabled ? DisplayMode.ComparedTeam : DisplayMode.Team
        break
      case MetricFinderKey.Player:
        metricToRank = resolvePlayerRankingRequestAdapter(payload, aggregation.id, contextMetadata)
        displayMode = contextMetadata.isVsEnabled
          ? contextMetadata.isMatch
            ? DisplayMode.MatchComparedPlayer
            : DisplayMode.ComparedPlayer
          : contextMetadata.isMatch
            ? DisplayMode.MatchPlayer
            : DisplayMode.Player
        break
      case MetricFinderKey.Match:
        metricToRank = resolveMatchRankingRequest(payload, aggregation.id)
        displayMode = DisplayMode.Match
        break
    }

    metricToRank.aggregationDataSource =
      metricToRank?.aggregationDataSource === 'CompetitionAverage' ? 'Average' : metricToRank?.aggregationDataSource

    return {
      metricToRank,
      displayMode,
      metricContext: payload.valueContext
    }
  }

  private _mapAndSortRanking(rankings: RankedMetric[]): RankedMetric[] {
    const _rankings = (rankings || []).map(this._mapRanking)
    const [excluded, included] = partition(_rankings, excludeNegativeRankingFromSortFn)

    return [...included, ...excluded]
  }

  private _mapRanking = (rank: RankedMetric) => {
    let returnRank = { ...rank }

    if (returnRank.position) {
      const positionData = {
        playerPosition: returnRank.position,
        playerPositionKey: PLAYER_POSITION_KEYS[returnRank.position],
        genericPositionKey: returnRank.genericPositionKey,
        specificPositionKey: returnRank.specificPositionKey
      }
      returnRank = {
        ...returnRank,
        position: this._demarcationPipe.transform(
          positionData,
          PLAYER_DEMARCATION_KEYS,
          PLAYER_DEMARCATION_FALLBACK_KEY,
          ' '
        )
      }
    }

    return returnRank
  }
}
